import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Features } from '@ultrasnip/contracts';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { FormAlert, PasswordField } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';

export const Route = createFileRoute('/_auth/reset-password')({
  // Needs outgoing email, which is switched off (see Features.email).
  beforeLoad: () => {
    if (!Features.email) throw redirect({ to: '/login' });
  },
  validateSearch: z.object({ token: z.string().max(512).optional(), error: z.string().max(64).optional() }),
  component: ResetPasswordScreen,
});

function ResetPasswordScreen() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!token || linkError) {
    return (
      <AuthLayout title="This link has expired">
        <Button render={<Link to="/forgot-password" />} nativeButton={false} className="h-9 w-full">
          Get a new link
        </Button>
      </AuthLayout>
    );
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 10) return setError('Use at least 10 characters.');
    if (password !== confirm) return setError('The two passwords do not match.');
    setPending(true);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    setPending(false);
    if (resetError) return setError(authErrorMessage(resetError));
    toast.success('Password changed. Sign in with your new password.');
    await navigate({ to: '/login' });
  };

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormAlert message={error} />
        <PasswordField
          label="New password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label="Repeat new password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" disabled={pending} className="mt-1 h-9 w-full">
          {pending && <Spinner />}
          Save new password
        </Button>
      </form>
    </AuthLayout>
  );
}
